import * as React from "react";
import { cn } from "../../../lib/cn";
import styled from "styled-components/macro";
import { motion } from "framer-motion";

/**
 * BoxReset
 *
 * A component that resets the box-border class
 * to the default value. Otherwise just a div or any other HTMLElement
 *
 * @argument {React.HTMLProps<React.ReactHTMLElement<"div">> & { as?: React.ElementType }} props
 * */
export const Box = ({ children, as, className, ...props }) => {
  return (
    <div
      as={as}
      className={cn("box-border", className)}
      {...props}
    >
      {children}
    </div>
  );
};

export const WithReset = React.forwardRef(
  /**
   * Withreset
   *
   * @argument {React.HTMLProps<React.ReactHTMLElement<"div">> & { as?: React.ElementType, boxOnly?: boolean }} props
   * */
  ({ children, as, className, boxOnly, ...props }, ref) => {
    return (
      <ResetWrapper
        ref={ref}
        as={as}
        className={cn(boxOnly ? "box-border" : "box-border translate-x-0 translate-y-0 scale-100 scale-x-100 scale-y-100 skew-x-0 skew-y-0 rotate-0", className)}
        {...props}
      >
        {children}
      </ResetWrapper>
    );
  },
);

export const WithResetMotion = motion(WithReset);

const ResetWrapper = styled.div`
  /*
    1. Prevent padding and border from affecting element width. (https://github.com/mozdevs/cssremedy/issues/4)
    2. Remove default margins and padding
    3. Reset all borders.
  */
  *,
  ::after,
  ::before,
  ::backdrop {
    box-sizing: border-box; /* 1 */
    border: 0 solid; /* 3 */
  }

  hr {
    height: 0; /* 1 */
    color: inherit; /* 2 */
    border-top-width: 1px; /* 3 */
  }

  abbr:where([title]) {
    -webkit-text-decoration: underline dotted;
    text-decoration: underline dotted;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: inherit;
    font-weight: inherit;
  }

  a {
    color: inherit;
    -webkit-text-decoration: inherit;
    text-decoration: inherit;
  }

  b,
  strong {
    font-weight: bolder;
  }

  code,
  kbd,
  samp,
  pre {
    font-family: var(--default-mono-font-family, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace); /* 1 */
    font-feature-settings: var(--default-mono-font-feature-settings, normal); /* 2 */
    font-variation-settings: var(--default-mono-font-variation-settings, normal); /* 3 */
    font-size: 1em; /* 4 */
  }

  small {
    font-size: 80%;
  }

  sub,
  sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
  }

  sub {
    bottom: -0.25em;
  }

  sup {
    top: -0.5em;
  }

  table {
    text-indent: 0; /* 1 */
    border-color: inherit; /* 2 */
    border-collapse: collapse; /* 3 */
  }

  :-moz-focusring {
    outline: auto;
  }

  /*
    Add the correct vertical alignment in Chrome and Firefox.
  */
  progress {
    vertical-align: baseline;
  }

  /*
    Add the correct display in Chrome and Safari.
  */
  summary {
    display: list-item;
  }

  /*
    Make lists unstyled by default.
  */
  ol,
  ul,
  menu {
    list-style: none;
  }

  /*
    1. Make replaced elements \`display: block\` by default. (https://github.com/mozdevs/cssremedy/issues/14)
    2. Add \`vertical-align: middle\` to align replaced elements more sensibly by default. (https://github.com/jensimmons/cssremedy/issues/14#issuecomment-634934210)
        This can trigger a poorly considered lint error in some tools but is included by design.
  */
  img,
  svg,
  video,
  canvas,
  audio,
  iframe,
  embed,
  object {
    display: block; /* 1 */
    vertical-align: middle; /* 2 */
  }

  /*
    Constrain images and videos to the parent width and preserve their intrinsic aspect ratio. (https://github.com/mozdevs/cssremedy/issues/14)
  */
  img,
  video {
    max-width: 100%;
    height: auto;
  }

  /*
    1. Inherit font styles in all browsers.
    2. Remove border radius in all browsers.
    3. Remove background color in all browsers.
    4. Ensure consistent opacity for disabled states in all browsers.
  */
  button,
  input,
  select,
  optgroup,
  textarea,
  ::file-selector-button {
    font: inherit; /* 1 */
    font-feature-settings: inherit; /* 1 */
    font-variation-settings: inherit; /* 1 */
    letter-spacing: inherit; /* 1 */
    color: inherit; /* 1 */
    background-color: transparent; /* 3 */
    opacity: 1; /* 4 */
  }

  /*
    Restore default font weight.
  */
  :where(select:is([multiple], [size])) optgroup {
    font-weight: bolder;
  }

  /*
    Restore indentation.
  */
  :where(select:is([multiple], [size])) optgroup option {
    padding-inline-start: 20px;
  }

  /*
    Restore space after button.
  */
  ::file-selector-button {
    margin-inline-end: 4px;
  }

  /*
    1. Reset the default placeholder opacity in Firefox. (https://github.com/tailwindlabs/tailwindcss/issues/3300)
    2. Set the default placeholder color to a semi-transparent version of the current text color.
  */
  ::placeholder {
    opacity: 1; /* 1 */
    color: color-mix(in oklab, currentColor 50%, transparent); /* 2 */
  }

  /*
    Prevent resizing textareas horizontally by default.
  */
  textarea {
    resize: vertical;
  }

  /*
    Remove the inner padding in Chrome and Safari on macOS.
  */
  ::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  /*
    1. Ensure date/time inputs have the same height when empty in iOS Safari.
    2. Ensure text alignment can be changed on date/time inputs in iOS Safari.
  */
  ::-webkit-date-and-time-value {
    min-height: 1lh; /* 1 */
    text-align: inherit; /* 2 */
  }

  /*
    Prevent height from changing on date/time inputs in macOS Safari when the input is set to \`display: block\`.
  */
  ::-webkit-datetime-edit {
    display: inline-flex;
  }

  /*
    Remove excess padding from pseudo-elements in date/time inputs to ensure consistent height across browsers.
  */
  ::-webkit-datetime-edit-fields-wrapper {
    padding: 0;
  }

  ::-webkit-datetime-edit,
  ::-webkit-datetime-edit-year-field,
  ::-webkit-datetime-edit-month-field,
  ::-webkit-datetime-edit-day-field,
  ::-webkit-datetime-edit-hour-field,
  ::-webkit-datetime-edit-minute-field,
  ::-webkit-datetime-edit-second-field,
  ::-webkit-datetime-edit-millisecond-field,
  ::-webkit-datetime-edit-meridiem-field {
    padding-block: 0;
  }

  /*
    Remove the additional \`:invalid\` styles in Firefox. (https://github.com/mozilla/gecko-dev/blob/2f9eacd9d3d995c937b4251a5557d95d494c9be1/layout/style/res/forms.css#L728-L737)
  */
  :-moz-ui-invalid {
    box-shadow: none;
  }

  /*
    Correct the inability to style the border radius in iOS Safari.
  */
  button,
  input:where([type="button"], [type="reset"], [type="submit"]),
  ::file-selector-button {
    appearance: button;
    margin: 0 !important;
  }

  /*
    Correct the cursor style of increment and decrement buttons in Safari.
  */
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    height: auto;
  }

  /*
    Make elements with the HTML hidden attribute stay hidden by default.
  */
  [hidden]:where(:not([hidden="until-found"])) {
    display: none !important;
  }
`;
